import {
    AfterViewInit,
    Component, ElementRef, HostListener,
    ViewChild,
    ViewContainerRef, HostBinding, ChangeDetectorRef, ChangeDetectionStrategy} from '@angular/core';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import {TooltipService} from "@irb-common/components/tooltip/tooltip.service";


const KEY_BACKSPACE = 8;
const KEY_DELETE = 46;
const KEY_F2 = 113;
const KEY_ENTER = 13;
const KEY_TAB = 9;
const KEY_ESCAPE = 27;

@Component({ standalone: false,
    selector: 'irb-numeric-cell-editor',
    template: `
        <span tooltip [tooltipTitle]="tooltiptext" [tooltipClass]="'numeric-cell-editor-tooltip'"
              [tooltipPosition]="'left'" [tooltipHorizontalOffset]='0'>
        <input #input (keydown)="onKeyDown($event)" [(ngModel)]="value" [class]=" !isValid? 'input-not-valid' : ''"
               (keyup)="onKeyUp()"
        style="width: 99%"/>
        </span>
    `,
    styleUrls : ['./numeric-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class NumericEditorComponent implements ICellEditorAngularComp, AfterViewInit {
    private params: any = {};
    public value: number;
    public highlightAllOnFocus: boolean = true;
    public isValid: boolean = true;
    public tooltiptext: string;
    private cancelBeforeStart: boolean = false;
    private originalValue: number;
    private asEditorWithBorder: boolean = false;

    @ViewChild('input', { read: ViewContainerRef }) public input: any;

    constructor(private element: ElementRef, private tts: TooltipService) {}

    @HostListener('mouseenter')
    onMouseEnter(): void {
        setTimeout(() => {
            this.validateTooltip();
        },0);
    }

    @HostBinding('class') get HeadingClass() {
        let hostClass = '';
        if (this.asEditorWithBorder) {
            hostClass = 'asEditorWithBorder';
            if (!this.isValid)
                hostClass += ' input-not-valid';
        }
        return hostClass;
    }

    agInit(params: any): void {
        this.params = params;
        this.tooltiptext = params.tooltiptext ? params.tooltiptext : '';
        this.originalValue = !params.value || params.value.toString() === '' ? 0 : params.value;
        this.asEditorWithBorder = params.asEditorWithBorder || false;
        this.setInitialState(this.params);

        // only start edit if key pressed is a number, not a letter
        this.cancelBeforeStart =
            params.charPress && '1234567890'.indexOf(params.charPress) < 0;
    }

    setInitialState(params: any) {
        let startValue;
        let highlightAllOnFocus = true;

        if (params.keyPress === KEY_BACKSPACE || params.keyPress === KEY_DELETE) {
            // if backspace or delete pressed, we clear the cell
            startValue = 0;
        } else if (params.charPress) {
            // if a letter was pressed, we start with the letter
            startValue = params.charPress;
            highlightAllOnFocus = false;
        } else {
            // otherwise we start with the current value
            startValue = params.value ? params.value : 0;
            if (params.keyPress === KEY_F2) {
                highlightAllOnFocus = false;
            }
        }

        this.value = startValue;
        this.highlightAllOnFocus = highlightAllOnFocus;
    }

    isCancelAfterEnd(): boolean {
        if (!this.isValid) {
            if (this.params.context
                && this.params.context.componentParent
                && this.params.context.componentParent.updateRow) {
                this.element.nativeElement.parentNode.focus();
                window.setTimeout(() => {
                    this.params.context.componentParent.updateRow(this.params, false);
                }, 10);
            }
        }
        return !this.isValid;
    }

    getValue(): any {
        if (!this.value) {
            this.value = 0;
        }
        this.value = this.value.toString() === '' ? 0 : this.value;
        if (parseFloat(this.originalValue.toString()) !== parseFloat(this.value.toString())) {

            this.params.data[this.params.colDef.field] = this.value;
            if (this.params.context
                && this.params.context.componentParent
                && this.params.context.componentParent.updateRow) {
                this.element.nativeElement.parentNode.focus();
                window.setTimeout(() => {
                    this.params.context.componentParent.updateRow(this.params, true);
                }, 10);
            }
        }
        return this.value;
    }

    isCancelBeforeStart(): boolean {
        return this.cancelBeforeStart;
    }

    onKeyDown(event: any): void {
        if (this.isLeftOrRight(event) || this.deleteOrBackspace(event)) {
            event.stopPropagation();
            return;
        }

        if (
            !this.finishedEditingPressed(event) &&
            !this.isKeyPressedNumeric(event)
        ) {
            if (event.preventDefault) { event.preventDefault(); }
        }
    }

    onKeyUp(): void {
        this.isValid = true;
        let validationReturn: boolean | string;
        if (this.params.validationFn) {
            validationReturn = this.params.validationFn(this.input.element.nativeElement.value, this.params);
            this.isValid = validationReturn === true ? true : false;
            if (!this.isValid) {
                if (typeof validationReturn === 'string' && validationReturn !== '') {
                    this.tooltiptext = validationReturn;
                }
                // if (event.preventDefault) { event.preventDefault(); }
                // event.stopPropagation();
                return;
            }
        }
        this.tooltiptext = null;
        this.tts.remove();
    }

    ngAfterViewInit() {
        window.setTimeout(() => {
            this.input.element.nativeElement.focus();
            if (this.highlightAllOnFocus) {
                this.input.element.nativeElement.select();

                this.highlightAllOnFocus = false;
            } else {
                const length = this.input.element.nativeElement.value
                    ? this.input.element.nativeElement.value.length
                    : 0;
                if (length > 0) {
                    this.input.element.nativeElement.setSelectionRange(length, length);
                }
            }

            this.input.element.nativeElement.focus();
            this.validateTooltip();
        });
    }

    validateTooltip() {
        let validationReturn: boolean | string;
        if (this.params.validationFn) {
            validationReturn = this.params.validationFn(this.input.element.nativeElement.value, this.params);
            this.isValid = validationReturn === true ? true : false;
            if (!this.isValid) {
                if (typeof validationReturn === 'string' && validationReturn !== '') {
                    this.tooltiptext = validationReturn;
                }
                return;
            }
        }
        this.tooltiptext = null;
        this.tts.remove();
    }

    private getCharCodeFromEvent(event: any): any {
        event = event || window.event;
        return typeof event.which === 'undefined' ? event.keyCode : event.which;
    }

    private isCharNumeric(charStr: string): boolean {
        return !!/\d/.test(charStr);
    }

    private isKeyPressedNumeric(event: any): boolean {
        const charCode = this.getCharCodeFromEvent(event);
        const charStr = event.key ? event.key : String.fromCharCode(charCode);
        let retValue = this.isCharNumeric(charStr);
        if (charStr === '.') {
            retValue = this.value.toString() !== '' ? true : false;
        }
        return retValue;
    }

    private deleteOrBackspace(event: any) {
        return (
            [KEY_DELETE, KEY_BACKSPACE, KEY_ESCAPE].indexOf(this.getCharCodeFromEvent(event)) > -1
        );
    }

    private isLeftOrRight(event: any) {
        return [37, 39].indexOf(this.getCharCodeFromEvent(event)) > -1;
    }

    private finishedEditingPressed(event: any) {
        const charCode = this.getCharCodeFromEvent(event);
        return charCode === KEY_ENTER || charCode === KEY_TAB;
    }
}
